'use client'

import { useId, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { EyeIcon, EyeOffIcon } from '@/components/icons/EyeIcon'
import { Input, labelClasses } from './Input'

type PasswordInputProps = {
  label?: string
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>

/**
 * `Input` for passwords, with a show/hide toggle. The toggle is disabled with
 * the field so it freezes along with the rest of a submitting form.
 */
export function PasswordInput({
  label,
  id,
  className = '',
  disabled,
  ...props
}: PasswordInputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const [visible, setVisible] = useState(false)

  return (
    <div data-component="PasswordInput" className="space-y-1">
      {label && (
        <label htmlFor={inputId} className={labelClasses}>
          {label}
        </label>
      )}
      <div className="relative">
        <Input
          {...props}
          id={inputId}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          className={`pr-12 ${className}`}
        />
        <button
          type="button"
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-controls={inputId}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className={
            'absolute inset-y-0 right-0 flex items-center px-3.5 rounded-r-lg ' +
            'text-mist hover:text-ink dark:text-cream/40 dark:hover:text-cream ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ' +
            'focus-visible:ring-carolina-dark/30 dark:focus-visible:ring-carolina/30 ' +
            'disabled:opacity-50 disabled:pointer-events-none transition-colors'
          }
        >
          {visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
        </button>
      </div>
    </div>
  )
}
